import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { useSendInvitationMutation } from '../../../../api/useSendInvitationMutation';
import { currentClientSelector } from '../../../../../AdviserArea/AdviserAreaSelector';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { updateEmail } from '../../../../../AdviserArea/AdviserAreaActions';
import { FormValues, validation } from './ClientEmailInvitation.meta';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';

interface Props {
  clientId: string;
  onSuccess(): void;
  onBack(): void;
  onClose(): void;
}

export const ClientEmailInvitation = ({ clientId, onSuccess, onBack, onClose }: Props) => {
  const [isComplete, setIsComplete] = useState(false);

  const emailInvitationRef = useRef<HTMLFormElement>(null);

  const currentClient = useSelector(currentClientSelector);

  const dispatch = useDispatch();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      email: currentClient?.Email || '',
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: sendInvitation,
    isLoading: isSendingInvitation,
    isSuccess: isSendInvitationSuccess,
    error: sendInvitationError,
    reset: resetSendInvitationMutation,
  } = useSendInvitationMutation();

  const onSubmit = async ({ email }: FormValues) => {
    try {
      await sendInvitation({ clientId, email });

      dispatch(updateEmail(clientId, email));

      if (emailInvitationRef.current) {
        emailInvitationRef.current.scrollIntoView({ block: 'start' });
      }

      onSuccess();

      setIsComplete(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <ModalBody css="height: 200px">
        {sendInvitationError && (
          <Alert
            message={sendInvitationError.message}
            onRequestClose={resetSendInvitationMutation}
            variant="error"
            my="16"
          />
        )}

        {isSendInvitationSuccess && (
          <Alert
            message="The invitation was successfully sent"
            onRequestClose={resetSendInvitationMutation}
            variant="success"
            my="16"
          />
        )}

        <Box
          id="email--invite-client"
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          ref={emailInvitationRef}
          mt="16"
        >
          <Input
            {...register('email')}
            label="Email invitation to:"
            type="text"
            formVariant={errors.email ? 'error' : undefined}
            helperText={errors.email?.message}
          />
        </Box>
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="secondary" leftIcon="fas fa-chevron-left" onClick={onBack} mr="auto">
          Back
        </Button>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          {isComplete ? 'Close' : 'Cancel'}
        </Button>

        <Button type="submit" form="email--invite-client" isLoading={isSendingInvitation}>
          Send
        </Button>
      </ModalFooter>
    </>
  );
};
